/**
 * No job left open — the iPad side's data and rules (threads/no-job-left-open/).
 *
 * The server (GET /api/field/left-open) says which of the tech's jobs are
 * still open and what each one needs: `ask` (no answer yet — On my way to
 * another job is blocked), `later_today`, `booked`, or `submit` (Can't finish,
 * Review & Submit not done). Everything here is pure or storage, so it is
 * unit-tested without rendering.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type LeftOpenState = "ask" | "later_today" | "booked" | "submit";
export type DeviceDot = "pass" | "fail" | "todo" | "gone" | "cant";

export interface LeftOpenDevice {
    linkId: string;
    label: string;
    location: string | null;
    serial: string | null;
    dot: DeviceDot;
}

export interface LeftOpenJob {
    jobId: string;
    displayNumber: string | null;
    customerName: string | null;
    siteName: string | null;
    siteLat: number | null;
    siteLng: number | null;
    status: string;
    arrivedAt: string | null;
    state: LeftOpenState;
    dots: DeviceDot[];
    devices: LeftOpenDevice[];
    laterBookedStart: string | null;
}

export interface LeftOpenResponse {
    switchOn: boolean;
    jobs: LeftOpenJob[];
    /** The company's office number (Settings → Company) for "Call the office". */
    officePhone?: string | null;
}

/** "8175550142" → "(817) 555-0142"; anything else is shown as typed. */
export function formatPhone(raw: string): string {
    const d = raw.replace(/\D/g, "");
    const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
    if (ten.length !== 10) return raw.trim();
    return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

export const LEFT_OPEN_KEY = ["field", "left-open"] as const;

/** The tech's open jobs. Fails closed: any error reads as switch off. */
export function useLeftOpenJobs(enabled: boolean) {
    return useQuery<LeftOpenResponse>({
        queryKey: LEFT_OPEN_KEY,
        queryFn: () => api.get<LeftOpenResponse>("/api/field/left-open"),
        enabled,
        staleTime: 30_000,
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
        retry: 1,
    });
}

// ── Reasons (the chips) ────────────────────────────────────────────────────

export const COMING_BACK_REASONS = [
    { value: "ran_out_of_time", label: "Out of time" },
    { value: "no_access", label: "No access" },
    { value: "parts_needed", label: "Need parts" },
    { value: "needs_customer", label: "Needs customer" },
    { value: "other", label: "Other" },
] as const;
export type ComingBackReason = (typeof COMING_BACK_REASONS)[number]["value"];

export const DEVICE_REASONS = [
    { value: "no_access", label: "No access" },
    { value: "no_water", label: "No water" },
    { value: "device_gone", label: "Removed" },
    { value: "ran_out_of_time", label: "Out of time" },
    { value: "needs_customer", label: "Needs customer" },
    { value: "other", label: "Other" },
] as const;
export type DeviceReason = (typeof DEVICE_REASONS)[number]["value"];

export type LeaveWhen = "later_today" | "another_day" | "booked";

export interface LeaveAnswerBody {
    choice: "coming_back" | "cant_finish";
    reason?: ComingBackReason | DeviceReason;
    when?: LeaveWhen;
    devices?: Array<{ linkId: string; reason: DeviceReason }>;
    tappedAt: string;
    partDispositions?: Array<{ partId: string; disposition: string; note?: string }>;
}

// ── Pure rules ─────────────────────────────────────────────────────────────

export const jobTitle = (j: Pick<LeftOpenJob, "siteName" | "customerName">): string =>
    j.siteName?.trim() || j.customerName?.trim() || "This job";

export const doneCount = (dots: DeviceDot[]): number => dots.filter((d) => d !== "todo").length;
export const testedCount = (dots: DeviceDot[]): number => dots.filter((d) => d === "pass" || d === "fail").length;
export const allHaveResult = (dots: DeviceDot[]): boolean => dots.length > 0 && dots.every((d) => d !== "todo");

/**
 * The job that blocks On my way to `targetJobId`: the first OTHER open job
 * still waiting for an answer that he hasn't snoozed with "I'm still here".
 */
export function blockingJob(
    jobs: LeftOpenJob[],
    targetJobId: string,
    isSnoozed: (jobId: string) => boolean,
): LeftOpenJob | null {
    return jobs.find((j) => j.jobId !== targetJobId && j.state === "ask" && !isSnoozed(j.jobId)) ?? null;
}

/** "Thu 10/8" — the booked visit chip. */
export function bookedLabel(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "Already booked";
    const day = d.toLocaleDateString("en-US", { weekday: "short", timeZone: "America/Chicago" });
    const md = d.toLocaleDateString("en-US", { month: "numeric", day: "numeric", timeZone: "America/Chicago" });
    return `${day} ${md} · already booked`;
}

// ── "I'm still here" + offline answers (per iPad, survives screen changes) ─

const STILL_HERE_KEY = "leftOpen.stillHere.v1";
const ANSWERED_KEY = "leftOpen.answeredOffline.v1";
export const STILL_HERE_NO_GPS_MS = 30 * 60_000;
/** On-site snooze lasts until GPS sees him leave (cleared then), capped. */
export const STILL_HERE_ON_SITE_MS = 4 * 3_600_000;

function readMap(key: string): Record<string, number> {
    try {
        const raw = localStorage.getItem(key);
        const v = raw ? (JSON.parse(raw) as unknown) : null;
        return v && typeof v === "object" ? (v as Record<string, number>) : {};
    } catch {
        return {};
    }
}
function writeMap(key: string, map: Record<string, number>): void {
    try {
        localStorage.setItem(key, JSON.stringify(map));
    } catch {
        /* private mode / full — the snooze just won't survive a reload */
    }
}

export function stillHereUntil(jobId: string): number {
    return readMap(STILL_HERE_KEY)[jobId] ?? 0;
}
export function isStillHere(jobId: string, now = Date.now()): boolean {
    return stillHereUntil(jobId) > now;
}
export function setStillHere(jobId: string, until: number): void {
    const m = readMap(STILL_HERE_KEY);
    m[jobId] = until;
    writeMap(STILL_HERE_KEY, m);
}
export function clearStillHere(jobId: string): void {
    const m = readMap(STILL_HERE_KEY);
    delete m[jobId];
    writeMap(STILL_HERE_KEY, m);
}

/** An answer queued offline: don't ask again until the server has it (max a day). */
export function markAnsweredOffline(jobId: string, now = Date.now()): void {
    const m = readMap(ANSWERED_KEY);
    m[jobId] = now;
    writeMap(ANSWERED_KEY, m);
}
export function answeredOffline(jobId: string, now = Date.now()): boolean {
    const t = readMap(ANSWERED_KEY)[jobId];
    return !!t && now - t < 24 * 3_600_000;
}

/** Distance in meters between two WGS84 points. */
export function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
